import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import api from "../../services/api";
import getUserIp from "../../services/getUserIp";
import ToastError from "../../toast/error/toastError";
import UserSession from "../UserSession";
import socket from "../useSocket";

const getUserData = async () => {
  const userLocaleData = await getUserIp();
  const { city, region, ip } = userLocaleData;
  const deviceUUID = UserSession.getUUID();
  return { lastIp: ip, lastLocation: city, lastRegion: region, lastDeviceUUID: deviceUUID };
};

const useAuth = () => {
  const history = useHistory();
  const location = useLocation(); // Para verificar a rota atual

  // Lista de rotas públicas que não devem redirecionar
  const publicRoutes = ["download", "webchat", "disconnect"];
  const isPublicRoute = publicRoutes.some(route =>
    location.pathname.includes(route)
  );

  // State definitions
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  const [status, setStatus] = useState(0);
  // Motivo do "ocupado" (status 3). Null nos demais.
  const [statusMessage, setStatusMessage] = useState(null);

  // Intercept request to add Authorization header if token exists
  api.interceptors.request.use(
    config => {
      const token = localStorage.getItem("token");
      if (token) {
        let parsedToken; try { parsedToken = JSON.parse(token) } catch (error) { parsedToken = token };
        config.headers["Authorization"] = `Bearer ${parsedToken}`;
        setIsAuth(true);
      }
      return config;
    },
    error => {
      return Promise.reject(error);
    }
  );

  // Intercept response to handle token refresh and authorization errors
  api.interceptors.response.use(
    response => {
      return response;
    },
    async error => {
      const originalRequest = error.config;

      // On 403 error, attempt to refresh the token
      if (error?.response?.status === 403 && !originalRequest._retry) {
        originalRequest._retry = true;
        const { lastIp, lastLocation, lastRegion, lastDeviceUUID } = await getUserData();
        const { data } = await api.post("/auth/refresh_token", { locale: { lastIp, lastLocation, lastRegion, lastDeviceUUID } });
        if (data) {
          localStorage.setItem("token", JSON.stringify(data.token));
          api.defaults.headers.Authorization = `Bearer ${data.token}`;
        }
        return api(originalRequest);
      }

      // On 401 error, remove token and logout user
      if (error?.response?.status === 401) {
        localStorage.removeItem("token");
        api.defaults.headers.Authorization = undefined;
        setIsAuth(false);

        // Só redireciona para login se NÃO estiver em uma rota pública
        if (!isPublicRoute) {
          history.push("/login");
        }
      }

      return Promise.reject(error);
    }
  );

  // On mount, refresh token if one exists
  useEffect(() => {
    const token = localStorage.getItem("token");
    (async () => {
      if (token) {
        try {
          const { lastIp, lastLocation, lastRegion, lastDeviceUUID } = await getUserData();
          const { data } = await api.post("/auth/refresh_token", { locale: { lastIp, lastLocation, lastRegion, lastDeviceUUID } });
          api.defaults.headers.Authorization = `Bearer ${data.token}`;
          setIsAuth(true);
          setUser(data.user);
          // Inicializa do backend (NÃO força online) — "ocupado" sobrevive a
          // reload/redeploy do frontend.
          setStatus(data.user?.status ?? 1)
          setStatusMessage(data.user?.statusMessage ?? null)
        } catch (err) {
          // Não redirecionar para login se estiver em uma rota pública
          if (!isPublicRoute) {
            ToastError(err);
          }
        }
      }
      setLoading(false);
    })();
  }, [isPublicRoute]); // Adicionamos isPublicRoute como dependência

  // Websocket to listen for user updates
  useEffect(() => {
    if (!user?.companyId) return;

    const handler = data => {
      if (data.action === "update" && data.user.id === user.id) {
        // Merge ao invés de replace — emits via register/disconnect trazem
        // payload parcial (sem queues/userPeriods). Substituir o objeto inteiro
        // perderia esses campos. Sincroniza status + mensagem de ocupado.
        setUser((prev) => ({ ...prev, ...data.user }));
        setStatus(data.user.status);
        setStatusMessage(data.user.statusMessage ?? null);
      }
    };
    socket.on("user", handler);

    // Cleanup: remove SÓ este handler (off sem callback removia todos os
    // listeners de 'user', inclusive de outros componentes).
    return () => {
      socket.off("user", handler);
    };
  }, [user?.id, user?.companyId]);

  // Redirect super admin users to system overview page (somente se NÃO estiver em rota pública)
  useEffect(() => {
    if (user?.profile === 'adminSuper' && !isPublicRoute) {
      history.push("/companies");
    }
  }, [user?.profile, isPublicRoute, history]);

  // Handle user login
  const handleLogin = async userData => {
    setLoading(true);
    try {
      const { lastIp, lastLocation, lastRegion, lastDeviceUUID } = await getUserData();
      userData = {
        ...userData,
        lastIp,
        lastLocation,
        lastRegion,
        lastDeviceUUID
      }
      const { data } = await api.post("/auth/login", userData);

      const { user: { companyId, id } } = data;

      localStorage.setItem("token", JSON.stringify(data.token));
      localStorage.setItem("companyId", companyId);
      localStorage.setItem("userId", id);

      api.defaults.headers.Authorization = `Bearer ${data.token}`;
      setUser(data.user);
      // Backend mantém ocupado no login — respeitar o status retornado.
      setStatus(data.user?.status ?? 1);
      setStatusMessage(data.user?.statusMessage ?? null);
      setIsAuth(true);
    } catch (err) {
      ToastError(err);
      history.push("/login");
    } finally {
      setLoading(false);
    }
  };

  // Handle user logout
  const handleLogout = async () => {
    setLoading(true);
    try {
      await api.delete("/auth/logout");
      setIsAuth(false);
      setUser({});
      localStorage.removeItem("token");
      localStorage.removeItem("companyId");
      localStorage.removeItem("userId");
      api.defaults.headers.Authorization = undefined;
      history.push("/login");
    } catch (err) {
      ToastError(err);
    } finally {
      setLoading(false);
      // Frontend não desconecta — backend encerra a sessão via /auth/logout
      // e o engine.io ping detecta a conexão morta server-side.
    }
  };

  const handleLogoutDisconnect = async () => {
    setLoading(true);
    try {
      await api.delete("/auth/logout");
      setIsAuth(false);
      setUser({});
      localStorage.removeItem("token");
      localStorage.removeItem("companyId");
      localStorage.removeItem("userId");
      api.defaults.headers.Authorization = undefined;

      history.push("/disconnect");
    } catch (err) {
      ToastError(err);
    } finally {
      setLoading(false);
      // Frontend não desconecta — backend encerra a sessão.
    }
  };

  const handleStatus = (currentStatus, message) => {
    setStatus(currentStatus);
    if (message !== undefined) setStatusMessage(message ?? null);
  };

  const handleUpdateUser = (data) => setUser(data);

  return { isAuth, user, loading, handleLogin, handleLogout, handleLogoutDisconnect, status, statusMessage, handleStatus, handleUpdateUser };
};

export default useAuth;